import React from 'react';
import { Check, FileText, ShoppingCart, ClipboardCheck } from 'lucide-react';
import { cn } from '@/lib/utils';

export type EstimateStep = 'details' | 'items' | 'finalize';

interface EstimateProgressProps {
  currentStep: EstimateStep;
  onStepClick?: (step: EstimateStep) => void;
  completedSteps?: EstimateStep[];
  className?: string;
}

const STEPS = [
  { id: 'details' as const, label: 'Details', icon: FileText, description: 'Basic info & customer' },
  { id: 'items' as const, label: 'Items', icon: ShoppingCart, description: 'Add line items' },
  { id: 'finalize' as const, label: 'Finalize', icon: ClipboardCheck, description: 'Terms & send' },
];

const EstimateProgress: React.FC<EstimateProgressProps> = ({
  currentStep,
  onStepClick,
  completedSteps = [],
  className,
}) => {
  const currentIndex = STEPS.findIndex(s => s.id === currentStep);

  const getStepStatus = (stepId: EstimateStep, index: number) => {
    if (completedSteps.includes(stepId)) return 'completed';
    if (stepId === currentStep) return 'current';
    if (index < currentIndex) return 'completed';
    return 'upcoming';
  };

  return (
    <div className={cn('w-full', className)}>
      {/* Mobile View - Compact */}
      <div className="flex md:hidden items-center justify-between px-2 py-3 bg-muted/50 rounded-lg">
        {STEPS.map((step, index) => {
          const status = getStepStatus(step.id, index);
          const Icon = step.icon;
          const isClickable = onStepClick && (status === 'completed' || status === 'current');
          
          return (
            <React.Fragment key={step.id}>
              <button
                type="button"
                onClick={() => isClickable && onStepClick?.(step.id)}
                disabled={!isClickable}
                className={cn(
                  'flex flex-col items-center gap-1 p-2 rounded-lg transition-all min-w-0 flex-1',
                  status === 'current' && 'bg-primary text-primary-foreground',
                  status === 'completed' && 'text-primary',
                  status === 'upcoming' && 'text-muted-foreground',
                  isClickable && 'cursor-pointer hover:bg-accent'
                )}
              >
                <div className={cn(
                  'flex items-center justify-center w-8 h-8 rounded-full border-2',
                  status === 'current' && 'border-primary-foreground bg-primary-foreground/20',
                  status === 'completed' && 'border-primary bg-primary text-primary-foreground',
                  status === 'upcoming' && 'border-muted-foreground/30'
                )}>
                  {status === 'completed' ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <Icon className="h-4 w-4" />
                  )}
                </div>
                <span className="text-xs font-medium truncate">{step.label}</span>
              </button>
              
              {index < STEPS.length - 1 && (
                <div className={cn(
                  'flex-shrink-0 w-8 h-0.5',
                  index < currentIndex ? 'bg-primary' : 'bg-muted-foreground/30'
                )} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Desktop View - Full */}
      <div className="hidden md:flex items-center justify-center">
        <div className="flex items-center gap-0">
          {STEPS.map((step, index) => {
            const status = getStepStatus(step.id, index);
            const Icon = step.icon;
            const isClickable = onStepClick && (status === 'completed' || status === 'current');

            return (
              <React.Fragment key={step.id}>
                <button
                  type="button"
                  onClick={() => isClickable && onStepClick?.(step.id)}
                  disabled={!isClickable}
                  className={cn(
                    'flex items-center gap-3 px-4 py-2 rounded-lg transition-all',
                    isClickable && 'cursor-pointer hover:bg-accent'
                  )}
                >
                  <div className={cn(
                    'flex items-center justify-center w-10 h-10 rounded-full border-2 transition-colors',
                    status === 'current' && 'border-primary bg-primary text-primary-foreground',
                    status === 'completed' && 'border-primary bg-primary text-primary-foreground',
                    status === 'upcoming' && 'border-muted-foreground/30 text-muted-foreground'
                  )}>
                    {status === 'completed' ? (
                      <Check className="h-5 w-5" />
                    ) : (
                      <Icon className="h-5 w-5" />
                    )}
                  </div>
                  
                  <div className="text-left">
                    <div className={cn(
                      'text-sm font-medium',
                      status === 'current' && 'text-foreground',
                      status === 'completed' && 'text-foreground',
                      status === 'upcoming' && 'text-muted-foreground'
                    )}>
                      {step.label}
                    </div>
                    <div className={cn(
                      'text-xs',
                      status === 'upcoming' ? 'text-muted-foreground/70' : 'text-muted-foreground'
                    )}>
                      {step.description}
                    </div>
                  </div>
                </button>

                {index < STEPS.length - 1 && (
                  <div className={cn(
                    'w-12 h-0.5 mx-2',
                    index < currentIndex ? 'bg-primary' : 'bg-muted-foreground/30'
                  )} />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EstimateProgress;
